import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { 
  Play, 
  Square, 
  Truck, 
  Wrench, 
  Home, 
  Clock, 
  MapPin,
  Coffee,
  BookOpen,
  ShoppingBag,
  Settings,
  FileText,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Trash2,
  RefreshCw,
  Send,
  Sunset,
  RotateCcw
} from 'lucide-react';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import DailyReportPanel from '../components/timetracker/DailyReportPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// Small pill rendered next to a job/event identifier when the time
// block (or active block, or aggregated job-card) belongs to a Calendar
// Event rather than a real Job. Keeps the UI honest about what's being
// tracked so admins reviewing timesheets can see EVENT rows at a glance.
const EventBadge = () => (
  <span
    className="inline-flex items-center text-[10px] font-bold tracking-wide bg-purple-100 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded ml-1"
    data-testid="event-badge"
  >
    EVENT
  </span>
);

// Small filled-amber star used everywhere in the app to indicate
// "current user is Team Lead of this job". Kept as a tiny helper here so
// callers don't have to remember the size/fill classes.
const LeadStar = ({ className = 'w-4 h-4' }) => (
  <Star
    className={`${className} fill-amber-500 text-amber-500 shrink-0`}
    data-testid="lead-star"
  />
);

// Render a single row inside the "Select a job..." dropdown. Handles both
// real-job entries (`_kind: 'job'`) and calendar-event entries
// (`_kind: 'event'`). Events get a small purple "EVENT" tag and show the
// event type as the secondary label. Jobs where the current user is the
// designated Team Lead get a filled amber star. Keeping this as a
// function (not a component) avoids upsetting Radix Select, which is
// picky about its SelectItem children.
const renderJobRow = (job, showJobNumbers, currentUserId) => {
  const isLead = job._kind === 'job' && !!currentUserId && job.team_lead_id === currentUserId;
  if (job._kind === 'event') {
    return (
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold tracking-wide bg-purple-100 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded">
          EVENT
        </span>
        <span className="font-medium truncate">{job.job_title}</span>
        {job.event_type ? (
          <span className="text-slate-500 text-xs ml-1 truncate">· {job.event_type}</span>
        ) : null}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      {isLead && <LeadStar />}
      <span className="font-medium">{showJobNumbers ? job.job_number : ''}</span>
      <span className="text-slate-500 text-sm ml-2 truncate">{job.job_title}</span>
    </div>
  );
};

// Tailwind classes to override the default shadcn SelectItem highlight
// (which is amber/accent) with a medium blue that visually pops against
// the alternate gray row backgrounds AND against the amber Lead Star.
// blue-200 is the sweet spot — clearly "this row is highlighted", not
// so dark it fights the text, doesn't touch the amber star hue.
// The `!` prefix forces override of the base component's focus:bg-accent
// rule. Scoped to this file — every other dropdown in the app is
// unaffected.
const JOB_ROW_HIGHLIGHT =
  'focus:!bg-blue-200 focus:!text-blue-900 ' +
  'data-[highlighted]:!bg-blue-200 data-[highlighted]:!text-blue-900 ' +
  'data-[state=checked]:!bg-blue-200 data-[state=checked]:!text-blue-900';

// Shared SelectContent renderer for the three job-picker dropdowns on
// this page. When `showSearch=true` (Director / admin / view_all_jobs)
// we render a sticky search input at the top and split the list into two
// groups: "ASSIGNED TO ME" at the top (events + jobs flagged
// `is_assigned_to_me`), then "ALL OTHER JOBS" below for the full backlog.
// Built for Derek Hoyle's Director-of-Operations workflow.
const filterJobs = (jobs, query) => {
  if (!query || !query.trim()) return jobs;
  const q = query.toLowerCase();
  return jobs.filter((j) => (
    (j.job_number || '').toLowerCase().includes(q)
    || (j.job_title || '').toLowerCase().includes(q)
    || (j.client_name || '').toLowerCase().includes(q)
    || (j.service_address || '').toLowerCase().includes(q)
    || (j.event_type || '').toLowerCase().includes(q)
  ));
};

const JobSelectList = ({ jobs, showJobNumbers, showSearch, query, setQuery, emptyMsg = 'No jobs assigned', currentUserId = null }) => {
  const filtered = filterJobs(jobs, query);
  // Events and jobs explicitly assigned to me (or with no flag — legacy
  // payload before include_all=true) go in the top bucket. Only entries
  // explicitly flagged `is_assigned_to_me === false` drop to the bottom.
  const mine = filtered.filter((j) => j._kind === 'event' || j.is_assigned_to_me !== false);
  const others = filtered.filter((j) => j._kind === 'job' && j.is_assigned_to_me === false);
  const hasResults = mine.length > 0 || others.length > 0;
  return (
    <SelectContent
      className="max-h-[55vh] w-[var(--radix-select-trigger-width)]"
      position="popper"
      side="bottom"
      align="start"
      sideOffset={6}
      avoidCollisions={true}
      // Keep the dropdown away from the iOS status bar / Dynamic Island /
      // iPad camera notch. 80px top, 56px bottom (clear of the bottom tab bar
      // on mobile). Radix uses this when it has to flip/squeeze the menu.
      collisionPadding={{ top: 80, bottom: 56, left: 8, right: 8 }}
    >
      {showSearch && (
        <div
          className="sticky top-0 z-20 -mx-1 -mt-1 mb-1 px-2 py-2
                     bg-amber-50 border-b-2 border-amber-300 rounded-t-md
                     shadow-sm"
        >
          <Input
            autoFocus
            data-testid="tracker-job-search-input"
            placeholder="🔎 Search by customer, address, job # or keyword…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            className="h-10 text-sm bg-white border-2 border-amber-400
                       focus-visible:ring-2 focus-visible:ring-amber-500
                       placeholder:text-slate-500"
          />
        </div>
      )}
      {!hasResults ? (
        <div className="p-3 text-sm text-slate-500">
          {query.trim()
            ? <>No jobs match &ldquo;{query}&rdquo;.</>
            : emptyMsg}
        </div>
      ) : (
        <>
          {mine.length > 0 && (
            <>
              {(showSearch && others.length > 0) && (
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-[60px] z-[5]">
                  ASSIGNED TO ME ({mine.length})
                </div>
              )}
              {mine.map((job) => (
                <SelectItem key={job.id} value={job.id} data-testid={`tracker-job-opt-${job.id}`} className={JOB_ROW_HIGHLIGHT}>
                  {renderJobRow(job, showJobNumbers, currentUserId)}
                </SelectItem>
              ))}
            </>
          )}
          {others.length > 0 && (
            <>
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-[60px] z-[5]">
                ALL OTHER JOBS ({others.length})
              </div>
              {others.map((job) => (
                <SelectItem key={job.id} value={job.id} data-testid={`tracker-job-opt-${job.id}`} className={JOB_ROW_HIGHLIGHT}>
                  {renderJobRow(job, showJobNumbers, currentUserId)}
                </SelectItem>
              ))}
            </>
          )}
        </>
      )}
    </SelectContent>
  );
};

// Shop activity categories
const SHOP_CATEGORIES = [
  { id: 'SHOP_PREP', label: 'Shop Prep', description: 'Shop preparation & setup', icon: Settings, paid: true },
  { id: 'TRAINING', label: 'Training', description: 'Training & development', icon: BookOpen, paid: true },
  { id: 'ERRANDS', label: 'Errands', description: 'Company errands & supply runs', icon: ShoppingBag, paid: true },
  { id: 'EQUIPMENT', label: 'Equipment', description: 'Equipment maintenance & prep', icon: Wrench, paid: true },
  { id: 'END_OF_DAY_CLEANUP', label: 'End of Day Cleanup', description: 'End of day vehicle & equipment cleanup', icon: Sunset, paid: true },
  { id: 'LUNCH', label: 'Lunch', description: 'Lunch break (unpaid)', icon: Coffee, paid: false },
  { id: 'OTHER', label: 'Other', description: 'Other overhead activity', icon: FileText, paid: true }
];

// Format minutes as Xh Ym
const formatMinutes = (mins) => {
  if (!mins && mins !== 0) return '0m';
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

// Format time from ISO string to local time
const formatTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Get LOCAL date string (YYYY-MM-DD) - NOT UTC
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function FieldTimeTrackerV2() {
  const navigate = useNavigate();
  const { token, user, updatePreference } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingBlockId, setDeletingBlockId] = useState(null);
  const [status, setStatus] = useState(null);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('SHOP_PREP');
  const [otherNotes, setOtherNotes] = useState('');
  // Director-of-Operations search box. Only rendered when the user has
  // permission to view all jobs (admin/super_user/view_all_jobs perm).
  // Stored at page-level so the same query persists across all three
  // dropdown surfaces (idle, post-arrival, edit-block).
  const [jobSearchQuery, setJobSearchQuery] = useState('');

  // Permission gate: can this user see ALL jobs (not just assigned-to-me)
  // in the Time Tracker dropdown, and use the search box?  Director Derek
  // Hoyle's requested workflow — admins, super-users, and anyone explicitly
  // granted `view_all_jobs` on the Permissions page.
  const canSeeAllJobs = (
    user?.role === 'admin'
    || user?.role === 'super_user'
    || Boolean(user?.permissions?.view_all_jobs)
  );
  
  // Date navigation - default to today in LOCAL timezone
  const [selectedDate, setSelectedDate] = useState(() => getLocalDateString());

  const showJobNumbers = user?.preferences?.showJobNumbers_timetracker !== false;
  const toggleJobNumbers = () => updatePreference('showJobNumbers_timetracker', !showJobNumbers);

  const isToday = selectedDate === getLocalDateString();
  
  // Get timezone offset
  const tzOffset = new Date().getTimezoneOffset();
  
  // Load data
  const loadData = useCallback(async (showRefreshToast = false) => {
    try {
      // Pull status, assigned jobs, and assigned calendar events in parallel.
      // Events come back from `/assigned-events` already filtered (PTO/Holiday
      // excluded server-side) and overlap-checked for the selected date.
      const [statusRes, jobsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/api/time-blocks/status?tz_offset=${tzOffset}&date=${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/time-blocks/assigned-jobs?date=${selectedDate}&tz_offset=${tzOffset}${canSeeAllJobs ? '&include_all=true' : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/time-blocks/assigned-events?date=${selectedDate}&tz_offset=${tzOffset}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),  // fail-open: an event 5xx must not break the tracker
      ]);
      
      setStatus(statusRes.data);
      const realJobs = (jobsRes.data || []).map(j => ({ ...j, _kind: 'job' }));
      // Wrap events to share the same minimal shape the Select expects.
      // We prefix the displayed id with `event:` to keep job-ids and event-ids
      // distinct in selectedJobId state (avoids accidental collisions when the
      // travel/work endpoints land in checkpoint B).
      const eventEntries = (eventsRes.data || []).map(ev => ({
        _kind: 'event',
        id: `event:${ev.id}`,            // unique selectedJobId namespace
        event_id: ev.id,
        job_number: '',                  // no job number for events
        job_title: ev.title,
        service_address: ev.location || '',
        client_name: ev.type,            // shows the event type as the secondary line
        date: ev.date,
        is_multi_day: false,
        job_day_id: null,
        // Event-specific extras for the future travel/work wiring + badge display:
        event_type: ev.type,
        event_color: ev.color,
        event_all_day: ev.all_day,
        event_start_time: ev.start_time,
        event_end_time: ev.end_time,
        event_notes: ev.notes,
      }));
      const newJobs = [...realJobs, ...eventEntries];
      setAssignedJobs(newJobs);

      // Clear selection if it's no longer in the list; auto-pick first
      // assigned-to-me job otherwise. For include_all (Director) users
      // we ONLY auto-pick from assigned-to-me jobs to avoid surprise-
      // selecting a random unassigned job at the top of the list.
      if (newJobs.length === 0) {
        setSelectedJobId('');
      } else if (!selectedJobId || !newJobs.find(j => j.id === selectedJobId)) {
        const firstAssigned = newJobs.find(j => j._kind !== 'job' || j.is_assigned_to_me !== false);
        if (firstAssigned) {
          setSelectedJobId(firstAssigned.id);
        } else {
          setSelectedJobId('');
        }
      }
      
      if (showRefreshToast) {
        toast.success('Refreshed');
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load tracker data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, tzOffset, selectedJobId, selectedDate, canSeeAllJobs]);
  
  // Manual refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData(true);
  };
  
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Overnight-shift safety net: if the backend tells us a block is currently
  // active (no end_time) AND that block lives on a different calendar date
  // than what the user is currently viewing, jump to the active block's
  // date so the running session is visible and the End / Switch buttons
  // stay reachable. Fixes the May 28 -> May 29 midnight rollover where
  // Drew & Dustin lost sight of their in-progress block.
  useEffect(() => {
    const active = status?.active_block;
    if (active?.date && active.date !== selectedDate) {
      setSelectedDate(active.date);
    }
  }, [status?.active_block?.id, status?.active_block?.date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh on visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadData]);
  
  // API Actions
  const startShop = async (categoryOverride = null) => {
    const category = categoryOverride || selectedCategory;
    
    if (category === 'OTHER' && !otherNotes.trim()) {
      toast.error('Please enter notes for Other category');
      return;
    }
    
    if (!token) {
      toast.error('Session expired. Please refresh the page.');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/time-blocks/start-shop?tz_offset=${tzOffset}`,
        { category: category, notes: otherNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Started ${SHOP_CATEGORIES.find(c => c.id === category)?.label || 'Shop'}`);
      setOtherNotes('');
      setSelectedCategory(category);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to start');
    } finally {
      setActionLoading(false);
    }
  };
  
  const startTravel = async () => {
    if (!selectedJobId) {
      toast.error('Please select a job first');
      return;
    }

    // Events and jobs both flow through /start-travel — events send
    // `event_id` (server stores it in the time block's job_id field for
    // aggregation parity) while jobs send `job_id`. Exactly one is
    // accepted by the backend, so we branch the payload here.
    const picked = assignedJobs.find(j => j.id === selectedJobId);
    const payload = picked?._kind === 'event'
      ? { event_id: picked.event_id }
      : { job_id: selectedJobId };

    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/time-blocks/start-travel?tz_offset=${tzOffset}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        picked?._kind === 'event'
          ? `Traveling to event: ${picked?.job_title || 'event'}`
          : `Traveling to ${picked?.job_title || picked?.job_number || 'job'}`
      );
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start travel');
    } finally {
      setActionLoading(false);
    }
  };
  
  const startWork = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/time-blocks/start-work?tz_offset=${tzOffset}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Started work');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start work');
    } finally {
      setActionLoading(false);
    }
  };
  
  const endBlock = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/time-blocks/end-block`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stopped timer');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to stop');
    } finally {
      setActionLoading(false);
    }
  };
  
  const returnToShop = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/time-blocks/return-to-shop`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Returning to shop');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start return travel');
    } finally {
      setActionLoading(false);
    }
  };
  
  const endDay = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/time-blocks/end-day?tz_offset=${tzOffset}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Day ended');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end day');
    } finally {
      setActionLoading(false);
    }
  };

  // Two-tap guard so accidental taps (especially on mobile) don't end the
  // workday and stop a running task. The user must confirm; if they tapped
  // by mistake, they can dismiss and pick up exactly where they were.
  const confirmEndDay = () => {
    const ok = window.confirm(
      "End your workday?\n\n" +
      "• Any running task will be stopped\n" +
      "• Your time blocks will be sent to today's timesheet\n" +
      "• You can still edit and resume work if needed\n" +
      "• Submit Timesheet stays available as usual"
    );
    if (ok) endDay();
  };
  
  const sendToTimesheet = async () => {
    // Use selected date (for past days) or today
    const dateStr = selectedDate;
    
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/time-blocks/send-to-timesheet/${dateStr}?tz_offset=${tzOffset}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      const totalMins = data.totals?.total_minutes || 0;
      toast.success(`Sent to timesheet: ${formatMinutes(totalMins)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to timesheet');
    } finally {
      setActionLoading(false);
    }
  };
  
  const deleteBlock = async (blockId) => {
    if (!token) {
      toast.error('Session expired. Please refresh the page.');
      return;
    }
    
    setDeletingBlockId(blockId);
    
    try {
      await axios.delete(`${API}/api/time-blocks/block/${blockId}?tz_offset=${tzOffset}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Block deleted');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingBlockId(null);
    }
  };
  
  // Current state
  const currentState = status?.current_state || 'IDLE';
  const activeBlock = status?.active_block;
  const totals = status?.totals || {};
  const blocks = status?.blocks_today || [];
  const byJob = status?.by_job || [];
  const byShopCategory = status?.by_shop_category || {};
  
  // State for expanded breakdown view
  const [showBreakdown, setShowBreakdown] = useState(false);
  
  // Format selected date for display
  const displayDate = new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });
  
  // Navigate to previous/next day
  const goToPreviousDay = () => {
    const date = new Date(selectedDate + 'T12:00:00');
    date.setDate(date.getDate() - 1);
    setSelectedDate(getLocalDateString(date));
  };
  
  const goToNextDay = () => {
    const date = new Date(selectedDate + 'T12:00:00');
    date.setDate(date.getDate() + 1);
    const today = getLocalDateString();
    // Don't allow going beyond today
    if (getLocalDateString(date) <= today) {
      setSelectedDate(getLocalDateString(date));
    }
  };
  
  const goToToday = () => {
    setSelectedDate(getLocalDateString());
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-100 pb-24">
      {/* Header */}
      <div className={`p-6 text-white ${
        currentState === 'IDLE' ? 'bg-slate-600' :
        currentState === 'SHOP' ? 'bg-blue-600' :
        currentState === 'TRAVEL' ? 'bg-amber-500' :
        currentState === 'WORK' ? 'bg-green-600' : 'bg-slate-600'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-xl font-bold">Time Tracker</h1>
            {/* Date Navigation */}
            <div className="flex items-center gap-2 mt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={goToPreviousDay}
                className="text-white hover:bg-white/20 h-7 w-7 p-0"
                data-testid="prev-day-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <button 
                onClick={goToToday}
                className="text-white/90 text-sm hover:text-white"
              >
                {displayDate}
                {!isToday && <span className="ml-1 text-xs">(tap for today)</span>}
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={goToNextDay}
                disabled={isToday}
                className="text-white hover:bg-white/20 h-7 w-7 p-0 disabled:opacity-30"
                data-testid="next-day-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleJobNumbers}
              className={`h-10 px-2 ${showJobNumbers ? 'text-white bg-white/20' : 'text-white/50 hover:bg-white/20'}`}
              data-testid="toggle-job-numbers-btn"
              title={showJobNumbers ? 'Hide job numbers' : 'Show job numbers'}
            >
              <span className="font-mono">#</span>
              <span className="ml-1 text-xs">{showJobNumbers ? 'On' : 'Off'}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-white hover:bg-white/20 h-10 w-10 p-0"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Current Status */}
        <div className="mt-4 flex items-center gap-3">
          {currentState === 'IDLE' && <Clock className="w-8 h-8" />}
          {currentState === 'SHOP' && <Home className="w-8 h-8" />}
          {currentState === 'TRAVEL' && <Truck className="w-8 h-8" />}
          {currentState === 'WORK' && <Wrench className="w-8 h-8" />}
          <div>
            <p className="text-sm text-white/70">Current Status</p>
            <p className="text-xl font-bold">
              {currentState === 'IDLE' ? 'Ready to Start' :
               currentState === 'SHOP' ? `Shop: ${activeBlock?.shop_category_label || 'Prep'}` :
               currentState === 'TRAVEL' ? `Traveling → ${showJobNumbers ? activeBlock?.job_number : (activeBlock?.job_title || 'Job')}` :
               currentState === 'WORK' ? `Working: ${showJobNumbers ? activeBlock?.job_number : (activeBlock?.job_title || 'Job')}` : currentState}
            </p>
          </div>
        </div>
        
        {/* Active timer indicator */}
        {activeBlock && (
          <div className="mt-3 bg-white/20 rounded-lg px-3 py-2 text-sm">
            Started {formatTime(activeBlock.start_time)}
            {activeBlock.service_address && (
              <span className="block text-white/70 text-xs truncate">{activeBlock.service_address}</span>
            )}
          </div>
        )}
      </div>
      
      {/* Day Totals */}
      <div className="px-4 -mt-4">
        <Card className="shadow-lg">
          <CardContent className="p-4">
            {!isToday && (
              <p className="text-xs text-center text-amber-600 mb-2 font-medium">
                Viewing past day
              </p>
            )}
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <p className="text-2xl font-bold text-blue-600">{formatMinutes(totals.shop_minutes)}</p>
                <p className="text-xs text-slate-500">Shop</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-500">{formatMinutes(totals.travel_minutes)}</p>
                <p className="text-xs text-slate-500">Travel</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{formatMinutes(totals.work_minutes)}</p>
                <p className="text-xs text-slate-500">Work</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{formatMinutes(totals.total_paid_minutes)}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
            {totals.unpaid_minutes > 0 && (
              <p className="text-xs text-center text-slate-400 mt-2">
                + {formatMinutes(totals.unpaid_minutes)} unpaid (lunch)
              </p>
            )}
            
            {/* Expandable Breakdown Toggle */}
            {(Object.keys(byShopCategory).length > 0 || byJob.length > 0) && (
              <button
                onClick={() => setShowBreakdown(!showBreakdown)}
                className="w-full mt-3 pt-3 border-t border-slate-100 flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-slate-700"
              >
                <span>{showBreakdown ? 'Hide' : 'Show'} Details</span>
                <ChevronRight className={`w-4 h-4 transition-transform ${showBreakdown ? 'rotate-90' : ''}`} />
              </button>
            )}
            
            {/* Granular Breakdown */}
            {showBreakdown && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-4">
                {/* Shop Category Breakdown */}
                {Object.keys(byShopCategory).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1">
                      <Home className="w-3 h-3" /> Shop Breakdown
                    </p>
                    <div className="space-y-1">
                      {Object.entries(byShopCategory).map(([category, data]) => {
                        const catInfo = SHOP_CATEGORIES.find(c => c.id === category);
                        const Icon = catInfo?.icon || Settings;
                        return (
                          <div key={category} className="flex items-center justify-between text-sm bg-blue-50 px-2 py-1.5 rounded">
                            <div className="flex items-center gap-2">
                              <Icon className="w-3.5 h-3.5 text-blue-500" />
                              <span className="text-slate-700">{catInfo?.label || category}</span>
                              {!data.paid && <Badge variant="outline" className="text-xs py-0 h-4">unpaid</Badge>}
                            </div>
                            <span className={`font-medium ${data.paid ? 'text-blue-700' : 'text-slate-400'}`}>
                              {formatMinutes(data.minutes)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Job Breakdown (Travel + Work) */}
                {byJob.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                      <Wrench className="w-3 h-3" /> Job Breakdown
                    </p>
                    <div className="space-y-1">
                      {byJob.map((job) => (
                        <div key={job.job_id} className="bg-slate-50 px-2 py-2 rounded">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm text-slate-800 flex items-center">
                              {job.is_event
                                ? (job.job_title || 'Event')
                                : (showJobNumbers
                                    ? (job.job_number || job.job_title || 'Job')
                                    : (job.service_address || job.job_title || 'Job'))}
                              {job.is_event && <EventBadge />}
                            </span>
                            <span className="font-bold text-sm text-slate-900">
                              {formatMinutes(job.travel_minutes + job.work_minutes)}
                            </span>
                          </div>
                          {job.service_address && (
                            <p className="text-xs text-slate-500 truncate">{job.service_address}</p>
                          )}
                          <div className="flex gap-3 mt-1 text-xs">
                            {job.travel_minutes > 0 && (
                              <span className="text-amber-600 flex items-center gap-1">
                                <Truck className="w-3 h-3" /> {formatMinutes(job.travel_minutes)}
                              </span>
                            )}
                            {job.work_minutes > 0 && (
                              <span className="text-green-600 flex items-center gap-1">
                                <Wrench className="w-3 h-3" /> {formatMinutes(job.work_minutes)}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Action Area — visible when viewing TODAY, or whenever the user has
          a running session (overnight shifts can keep an active block alive
          past midnight; the End / Switch buttons must always be reachable). */}
      {(isToday || status?.active_block) && (
      <div className="p-4">
        {/* Overnight-shift banner: shown when the session started on a
            previous calendar day so the user knows exactly which shift
            they're closing out. */}
        {status?.active_block && status.active_block.date !== getLocalDateString() && (
          <div
            className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800 flex items-start gap-2"
            data-testid="overnight-session-banner"
          >
            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
            <p className="leading-snug">
              Tracking session in progress since{' '}
              <span className="font-semibold">{formatTime(status.active_block.start_time)}</span>
              {' '}on{' '}
              <span className="font-semibold">
                {new Date(status.active_block.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
              . End or switch your session below.
            </p>
          </div>
        )}
        <Card className="shadow-lg">
          <CardContent className="p-4">
            
            {/* IDLE State - Start Day */}
            {currentState === 'IDLE' && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600 text-center">Select activity to start your day</p>
                
                {/* Category Selection */}
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="h-14" data-testid="shop-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SHOP_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <cat.icon className="w-4 h-4" />
                          <span>{cat.label}</span>
                          {!cat.paid && <Badge variant="outline" className="text-xs">Unpaid</Badge>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* "Describe this activity" — shown for ALL categories,
                    matching the desktop Timesheet behavior. Required for
                    OTHER, optional for everything else. */}
                {(() => {
                  const selectedCat = SHOP_CATEGORIES.find(c => c.id === selectedCategory);
                  if (!selectedCat) return null;
                  const isOther = selectedCat.id === 'OTHER';
                  return (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-slate-600">
                        Describe this activity {isOther && <span className="text-red-600">*</span>}
                      </p>
                      <Input
                        value={otherNotes}
                        onChange={(e) => setOtherNotes(e.target.value)}
                        placeholder={
                          isOther
                            ? 'Describe the activity…'
                            : `What did you work on during ${selectedCat.label}? (optional)`
                        }
                        className="h-12"
                        data-testid="shop-activity-description"
                      />
                      {!isOther && (
                        <p className="text-[11px] text-slate-400">
                          Optional — adds context to this entry on reports.
                        </p>
                      )}
                    </div>
                  );
                })()}
                
                <Button
                  onClick={() => startShop()}
                  disabled={actionLoading || (selectedCategory === 'OTHER' && !otherNotes.trim())}
                  className="w-full h-16 text-lg font-bold bg-green-600 hover:bg-green-700"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Play className="w-5 h-5 mr-2" />}
                  Start Shop Activity
                </Button>
                
                {/* Direct to Job Option - only show at start of day */}
                {assignedJobs.length > 0 && blocks.length === 0 && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-slate-600 mb-2 font-medium">Or go directly to a job:</p>
                    <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                      <SelectTrigger className="h-14" data-testid="tracker-job-select-idle">
                        <SelectValue placeholder="Select a job..." />
                      </SelectTrigger>
                      <JobSelectList
                        jobs={assignedJobs}
                        showJobNumbers={showJobNumbers}
                        showSearch={canSeeAllJobs}
                        query={jobSearchQuery}
                        setQuery={setJobSearchQuery}
                        currentUserId={user?.id}
                      />
                    </Select>
                    <Button
                      onClick={startTravel}
                      disabled={actionLoading || !selectedJobId}
                      className="w-full h-14 mt-2 text-lg font-bold bg-amber-500 hover:bg-amber-600"
                    >
                      {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Truck className="w-5 h-5 mr-2" />}
                      Travel to Job
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* SHOP State - At Shop */}
            {currentState === 'SHOP' && (
              <div className="space-y-4">
                {/* Job Selection */}
                <div>
                  <p className="text-sm text-slate-600 mb-2">Select job to travel to</p>
                  <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                    <SelectTrigger className="h-14" data-testid="tracker-job-select-shop">
                      <SelectValue placeholder="Select a job..." />
                    </SelectTrigger>
                    <JobSelectList
                      jobs={assignedJobs}
                      showJobNumbers={showJobNumbers}
                      showSearch={canSeeAllJobs}
                      query={jobSearchQuery}
                      setQuery={setJobSearchQuery}
                    />
                  </Select>
                </div>
                
                {/* Leave for Job */}
                <Button
                  onClick={startTravel}
                  disabled={actionLoading || !selectedJobId}
                  className="w-full h-16 text-lg font-bold bg-amber-500 hover:bg-amber-600"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Truck className="w-5 h-5 mr-2" />}
                  Leave for Job
                </Button>
                
                {/* Switch Shop Activity */}
                <div className="pt-3 border-t">
                  <p className="text-sm text-slate-600 mb-3">Switch activity:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {SHOP_CATEGORIES.filter(c => c.id !== activeBlock?.shop_category).map(cat => (
                      <Button
                        key={cat.id}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (cat.id === 'OTHER') {
                            setSelectedCategory(cat.id);
                            // User needs to enter notes first, don't auto-start
                          } else {
                            startShop(cat.id);
                          }
                        }}
                        disabled={actionLoading}
                        className={`h-12 text-xs justify-start px-2 ${!cat.paid ? 'border-slate-300 text-slate-600' : ''}`}
                      >
                        <cat.icon className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">{cat.label} →</span>
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    Stops current timer & starts new one
                  </p>
                </div>
                
                {/* End My Day — closes any running block, pushes day to
                    timesheet so payroll has it, but does NOT submit. The
                    user can keep editing or resume work after if needed. */}
                <Button
                  onClick={confirmEndDay}
                  disabled={actionLoading}
                  className="w-full h-12 font-medium bg-red-600 hover:bg-red-700 text-white"
                  data-testid="end-day-shop-btn"
                >
                  End My Day
                </Button>
              </div>
            )}
            
            {/* TRAVEL State - On the way to job or returning to shop */}
            {currentState === 'TRAVEL' && (
              <div className="space-y-4">
                <div className="text-center py-4 bg-amber-50 rounded-lg">
                  <Truck className="w-10 h-10 mx-auto text-amber-500 mb-2" />
                  {activeBlock?.return_to_shop ? (
                    activeBlock?.job_id ? (
                      <>
                        <p className="text-sm text-amber-700">Returning to shop from</p>
                        <p className="text-xl font-bold text-amber-800">
                          {activeBlock?.is_event
                            ? (activeBlock?.job_title || 'Event')
                            : (showJobNumbers
                                ? (activeBlock?.job_number || activeBlock?.job_title || 'Job')
                                : (activeBlock?.job_title || 'Job'))}
                          {activeBlock?.is_event && <EventBadge />}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-amber-700">Traveling to</p>
                        <p className="text-xl font-bold text-amber-800">Shop</p>
                      </>
                    )
                  ) : (
                    <>
                      <p className="text-sm text-amber-700">
                        {activeBlock?.is_event ? 'Traveling to event' : 'Traveling to'}
                      </p>
                      <p className="text-xl font-bold text-amber-800">
                        {activeBlock?.is_event
                          ? (activeBlock?.job_title || 'Event')
                          : (showJobNumbers
                              ? (activeBlock?.job_number || activeBlock?.job_title || 'Job')
                              : (activeBlock?.job_title || 'Job'))}
                        {activeBlock?.is_event && <EventBadge />}
                      </p>
                    </>
                  )}
                  {activeBlock?.service_address && (
                    <p className="text-sm text-amber-600 mt-1">{activeBlock.service_address}</p>
                  )}
                </div>
                
                {activeBlock?.return_to_shop ? (
                  /* Returning to shop - show "Arrived at Shop" button */
                  <Button
                    onClick={endBlock}
                    disabled={actionLoading}
                    className="w-full h-16 text-lg font-bold bg-blue-600 hover:bg-blue-700"
                  >
                    {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Home className="w-5 h-5 mr-2" />}
                    Arrived at Shop
                  </Button>
                ) : (
                  /* Going to job - show "Arrived - Start Work" button */
                  <Button
                    onClick={startWork}
                    disabled={actionLoading}
                    className="w-full h-16 text-lg font-bold bg-green-600 hover:bg-green-700"
                  >
                    {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <MapPin className="w-5 h-5 mr-2" />}
                    Arrived - Start Work
                  </Button>
                )}
              </div>
            )}
            
            {/* WORK State - On job site */}
            {currentState === 'WORK' && (
              <div className="space-y-4">
                <div className="text-center py-4 bg-green-50 rounded-lg">
                  <Wrench className="w-10 h-10 mx-auto text-green-500 mb-2" />
                  <p className="text-sm text-green-700">
                    {activeBlock?.is_event ? 'Working event' : 'Working on'}
                  </p>
                  <p className="text-xl font-bold text-green-800">
                    {activeBlock?.is_event
                      ? (activeBlock?.job_title || 'Event')
                      : (showJobNumbers
                          ? (activeBlock?.job_number || activeBlock?.job_title || activeBlock?.service_address || 'Job')
                          : (activeBlock?.job_title || activeBlock?.service_address || 'Job'))}
                    {activeBlock?.is_event && <EventBadge />}
                  </p>
                  {activeBlock?.service_address && (
                    <p className="text-sm text-green-600 mt-1">{activeBlock.service_address}</p>
                  )}
                </div>
                
                <Button
                  onClick={endBlock}
                  disabled={actionLoading}
                  className="w-full h-16 text-lg font-bold bg-red-500 hover:bg-red-600"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Square className="w-5 h-5 mr-2" />}
                  Finish Job
                </Button>
                
                {/* Return to Shop option - travel back attributed to this job */}
                <Button
                  onClick={returnToShop}
                  disabled={actionLoading}
                  variant="outline"
                  className="w-full h-14 text-base font-medium border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="return-to-shop-from-job-btn"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <RotateCcw className="w-5 h-5 mr-2" />}
                  Return to Shop
                  <span className="text-xs ml-2 text-amber-500">(tracks travel)</span>
                </Button>
              </div>
            )}
            
            {/* After finishing work - show options */}
            {currentState === 'IDLE' && blocks.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-slate-600 mb-3">Continue your day:</p>
                
                {/* Go to another job */}
                <div className="space-y-2">
                  <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                    <SelectTrigger className="h-14" data-testid="tracker-job-select-next">
                      <SelectValue placeholder="Select next job..." />
                    </SelectTrigger>
                    <JobSelectList
                      jobs={assignedJobs}
                      showJobNumbers={showJobNumbers}
                      showSearch={canSeeAllJobs}
                      query={jobSearchQuery}
                      setQuery={setJobSearchQuery}
                      currentUserId={user?.id}
                    />
                  </Select>
                  
                  <Button
                    onClick={startTravel}
                    disabled={actionLoading || !selectedJobId}
                    className="w-full h-12 bg-amber-500 hover:bg-amber-600"
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    Travel to Next Job
                  </Button>
                </div>
                
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={returnToShop}
                    disabled={actionLoading}
                    variant="outline"
                    className="flex-1 h-12"
                    data-testid="return-shop-btn"
                  >
                    <Home className="w-4 h-4 mr-1" />
                    Return to Shop
                  </Button>
                  <Button
                    onClick={confirmEndDay}
                    disabled={actionLoading}
                    className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="end-day-idle-btn"
                  >
                    End My Day
                  </Button>
                </div>
              </div>
            )}
            
          </CardContent>
        </Card>
      </div>
      )}
      
      {/* Day's Timeline */}
      {blocks.length > 0 && (
        <div className="px-4">
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">
                {isToday ? "Today's Time Blocks" : "Time Blocks"}
              </h3>
              <div className="space-y-2">
                {blocks.slice().reverse().map((block) => (
                  <div 
                    key={block.id} 
                    className={`flex items-center gap-3 p-2 rounded-lg text-sm ${
                      block.block_type === 'SHOP' ? 'bg-blue-50' :
                      block.block_type === 'TRAVEL' ? 'bg-amber-50' :
                      block.block_type === 'WORK' ? 'bg-green-50' : 'bg-slate-50'
                    }`}
                  >
                    <div className="text-slate-500 text-xs w-20">
                      {formatTime(block.start_time)}
                      {block.end_time && (
                        <>
                          <br />
                          <span className="text-slate-400">→ {formatTime(block.end_time)}</span>
                        </>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          block.block_type === 'SHOP' ? 'border-blue-300 text-blue-700' :
                          block.block_type === 'TRAVEL' ? 'border-amber-300 text-amber-700' :
                          'border-green-300 text-green-700'
                        }`}
                      >
                        {block.block_type}
                      </Badge>
                      {block.is_event ? (
                        <>
                          <span className="ml-2 text-slate-700">{block.job_title || 'Event'}</span>
                          <EventBadge />
                        </>
                      ) : (
                        <>
                          {block.job_number && showJobNumbers && (
                            <span className="ml-2 text-slate-700">{block.job_number}</span>
                          )}
                          {block.job_number && !showJobNumbers && block.job_title && (
                            <span className="ml-2 text-slate-700">{block.job_title}</span>
                          )}
                        </>
                      )}
                      {block.shop_category_label && (
                        <span className="ml-2 text-slate-600">{block.shop_category_label}</span>
                      )}
                      {/* Inline notes — Errands free-text + any worker notes
                          the user typed on the block. Shown directly so the
                          user doesn't have to tap into Edit just to see what
                          a block was about. Skip only when the note text
                          duplicates an already-rendered label on this row. */}
                      {(() => {
                        const noteStr = (block.notes || '').trim();
                        if (!noteStr) return null;
                        const labels = [
                          (block.shop_category_label || '').trim().toLowerCase(),
                          (block.job_title || '').trim().toLowerCase(),
                          (block.job_number || '').trim().toLowerCase(),
                        ];
                        if (labels.includes(noteStr.toLowerCase())) return null;
                        return (
                          <div
                            className="mt-1 text-xs text-slate-600 italic leading-snug break-words"
                            data-testid={`block-notes-${block.id || ''}`}
                          >
                            {noteStr}
                          </div>
                        );
                      })()}
                    </div>
                    
                    <div className="text-right">
                      <span className="font-bold text-slate-800">
                        {block.minutes ? formatMinutes(block.minutes) : '...'}
                      </span>
                      {!block.is_paid && (
                        <span className="text-xs text-slate-400 block">unpaid</span>
                      )}
                    </div>
                    
                    <button
                      type="button"
                      className={`h-10 w-10 p-0 flex items-center justify-center rounded-md flex-shrink-0 ${
                        deletingBlockId === block.id 
                          ? 'bg-red-100 text-red-600' 
                          : 'text-red-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100'
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        deleteBlock(block.id);
                      }}
                      disabled={deletingBlockId === block.id}
                      data-testid={`delete-block-${block.id}`}
                    >
                      {deletingBlockId === block.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Team Lead Daily Report — hidden entirely when the current user
          is not a team lead on any job for this date. */}
      <DailyReportPanel userId={user?.id} date={selectedDate} token={token} />

      {/* Send to Timesheet - show when viewing past day OR when day is ended */}
      {blocks.length > 0 && (currentState === 'IDLE' || currentState === 'VIEWING_PAST') && (
        <div className="px-4 mt-4">
          <Button
            onClick={sendToTimesheet}
            disabled={actionLoading}
            className="w-full h-14 text-lg bg-indigo-600 hover:bg-indigo-700"
            data-testid="send-to-timesheet-btn"
          >
            {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
            Send to Timesheet
          </Button>
        </div>
      )}
    </div>
  );
}
